import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  TrendingUp, AlertTriangle, Gauge,
  ArrowUpRight, ArrowDownRight, Minus
} from 'lucide-react';

export const DashboardComparison = ({ stats, planUsage, theme, navigate }) => {
  const { dm, cardBg, textPrimary, textSecondary, textMuted, borderColor } = theme;

  return (
    <div className="grid lg:grid-cols-2 gap-5 mb-6">
      {/* Ranking Comparativo */}
      <Card className={`rounded-xl border shadow-sm ${cardBg} transition-colors duration-300`} data-testid="comparison-card">
        <CardHeader className="pb-3">
          <CardTitle className={`text-base font-semibold ${textPrimary} flex items-center gap-2`}>
            <TrendingUp className="w-5 h-5 text-[#C5A059]" />
            Ranking Comparativo
          </CardTitle>
          <p className={`text-xs ${textMuted}`}>Período atual vs. período anterior</p>
        </CardHeader>
        <CardContent>
          {stats?.comparison?.has_previous_data ? (
            <div className="space-y-4">
              {[
                { label: 'Média Geral', current: stats.average_rating, prev: stats.comparison.prev_average_rating, suffix: '/5' },
                { label: 'Satisfação', current: stats.satisfaction_percentage, prev: stats.comparison.prev_satisfaction, suffix: '%' },
                { label: 'Produtos', current: stats.ratings_by_category.food, prev: stats.comparison.prev_food, suffix: '/5' },
                { label: 'Atendimento', current: stats.ratings_by_category.service, prev: stats.comparison.prev_service, suffix: '/5' },
                { label: 'Ambiente', current: stats.ratings_by_category.ambiance, prev: stats.comparison.prev_ambiance, suffix: '/5' },
              ].map(({ label, current, prev, suffix }) => {
                const diff = current - prev;
                const isUp = diff > 0;
                const isDown = diff < 0;
                return (
                  <div key={label} className={`flex items-center justify-between py-2 border-b ${borderColor} last:border-0`}>
                    <span className={`text-sm font-medium ${dm ? 'text-slate-300' : 'text-slate-600'}`}>{label}</span>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm ${textMuted}`}>{prev}{suffix}</span>
                      <span className={dm ? 'text-slate-600' : 'text-slate-300'}>→</span>
                      <span className={`text-sm font-bold ${textPrimary}`}>{current}{suffix}</span>
                      <span className={`flex items-center text-xs font-semibold px-2 py-0.5 rounded-full ${
                        isUp ? 'bg-emerald-50 text-emerald-600' : isDown ? 'bg-red-50 text-red-600' : 'bg-slate-50 text-slate-500'
                      }`}>
                        {isUp ? <ArrowUpRight className="w-3 h-3 mr-0.5" /> : isDown ? <ArrowDownRight className="w-3 h-3 mr-0.5" /> : <Minus className="w-3 h-3 mr-0.5" />}
                        {Math.abs(diff).toFixed(1)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <TrendingUp className="w-10 h-10 text-slate-200 mx-auto mb-2" />
              <p className="text-sm text-slate-400">Dados comparativos aparecerao após o primeiro período completo</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Uso do Plano */}
      <Card className={`rounded-xl border shadow-sm ${cardBg} transition-colors duration-300`} data-testid="plan-usage-card">
        <CardHeader className="pb-3">
          <CardTitle className={`text-base font-semibold ${textPrimary} flex items-center gap-2`}>
            <Gauge className="w-5 h-5 text-[#0A2647]" />
            Uso do Plano
          </CardTitle>
          <p className={`text-xs ${textMuted}`}>Avaliacoes deste mes</p>
        </CardHeader>
        <CardContent>
          {planUsage ? (
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <span className={`text-sm font-medium ${dm ? 'text-slate-300' : 'text-slate-600'}`}>Plano Atual</span>
                <span className={`text-sm font-bold px-3 py-1 rounded-full ${dm ? 'text-[#C5A059] bg-[#C5A059]/10' : 'text-[#0A2647] bg-[#0A2647]/5'}`}>{planUsage.plan_name}</span>
              </div>
              {planUsage.is_unlimited ? (
                <div className="text-center py-4">
                  <p className="text-3xl font-bold text-[#C5A059]">{planUsage.monthly_count}</p>
                  <p className={`text-sm ${textMuted} mt-1`}>avaliacoes este mes (ilimitado)</p>
                </div>
              ) : (
                <>
                  <div className="text-center">
                    <p className={`text-3xl font-bold ${textPrimary}`}>{planUsage.monthly_count}<span className={`text-lg ${textMuted} font-normal`}>/{planUsage.monthly_limit}</span></p>
                    <p className={`text-xs ${textMuted} mt-1`}>avaliacoes utilizadas</p>
                  </div>
                  <div className={`w-full ${dm ? 'bg-slate-700' : 'bg-slate-100'} rounded-full h-3 overflow-hidden`}>
                    <div
                      className={`h-full rounded-full transition-all ${
                        planUsage.usage_percentage >= 90 ? 'bg-red-500' : planUsage.usage_percentage >= 70 ? 'bg-amber-500' : 'bg-emerald-500'
                      }`}
                      style={{ width: `${Math.min(planUsage.usage_percentage, 100)}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className={textMuted}>{planUsage.usage_percentage}% utilizado</span>
                    <span className={`font-semibold ${planUsage.remaining <= 5 ? 'text-red-500' : textSecondary}`}>
                      {planUsage.remaining} restantes
                    </span>
                  </div>
                  {planUsage.usage_percentage >= 80 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-semibold text-amber-800">Limite próximo!</p>
                        <p className="text-xs text-amber-600">Faca upgrade para não perder avaliacoes.</p>
                        <Button size="sm" className="mt-2 h-7 text-xs btn-secondary" onClick={() => navigate('/plans')}>Ver Planos</Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Gauge className="w-10 h-10 text-slate-200 mx-auto mb-2" />
              <p className="text-sm text-slate-400">Carregando...</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
